import * as React from "react"
import { Plus } from "lucide-react"
import { Badge } from "@/components/ui/Badge"
import { Card } from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import { cn } from "@/lib/utils"
import type { Role } from "../types"

type Props = {
  roles: Role[]
  selectedId: string | null
  onSelect: (id: string) => void
  canCreate: boolean
  onCreate: (name: string) => void
  creating: boolean
}

export default function RoleList({ roles, selectedId, onSelect, canCreate, onCreate, creating }: Props) {
  const [adding, setAdding] = React.useState(false)
  const [name, setName] = React.useState("")

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    onCreate(trimmed)
    setName("")
    setAdding(false)
  }

  return (
    <Card className="flex flex-col divide-y">
      {roles.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => onSelect(r.id)}
          className={cn(
            "relative flex flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors cursor-pointer",
            "first:rounded-t-lg last:rounded-b-lg",
            r.id === selectedId ? "task-notch" : "hover:bg-muted/50",
          )}
        >
          {r.id === selectedId && (
            <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-primary" />
          )}
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{r.name}</span>
            {r.system && <Badge>System</Badge>}
          </span>
          <span className="text-xs text-muted-foreground">
            {r.people_count === 1 ? "1 person" : `${r.people_count} people`}
          </span>
        </button>
      ))}

      {canCreate && (
        adding ? (
          <form onSubmit={submit} className="px-4 py-3">
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setAdding(false)}
              onBlur={() => name.trim() === "" && setAdding(false)}
              placeholder="Role name — Enter to create"
              disabled={creating}
            />
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-2 px-4 py-3 text-sm text-link hover:bg-muted/50 transition-colors cursor-pointer last:rounded-b-lg"
          >
            <Plus size={16} /> New role
          </button>
        )
      )}
    </Card>
  )
}
